import {MarkdownEditor} from '@github-ui/markdown-editor'
import {DiffIgnoredIcon} from '@primer/octicons-react'
import {Box} from '@primer/styled-react'
import {clsx} from 'clsx'
import {createContext, type DOMAttributes, type Ref, useContext, useEffect, useMemo, useRef, useState} from 'react'

import type {RepoSubject, SubjectType} from '../subject'
import styles from './SlashCommandsProvider.module.css'
// Define the interface for the SlashCommandExpanderElement
interface SlashCommandExpanderElement extends HTMLElement {
  reconnect?: () => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CustomElement<T> = Partial<T & DOMAttributes<T> & {children: any}>

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'slash-command-expander': CustomElement<{
        keys: string
        ref?: Ref<SlashCommandExpanderElement>
      }>
    }
  }
}

interface SlashCommandButtonContext {
  onInsertText: (text: string) => void
}
const SlashCommandButtonContext = createContext<SlashCommandButtonContext | null>(null)

interface SlashCommandProviderProps extends SlashCommandButtonContext {
  children: React.ReactNode
  onSave?: (...args: unknown[]) => void
  subject: RepoSubject
}

type Surface = 'issue_comment' | 'issue_body' | 'discussion' | 'pull_request' | 'commit'

function getSurface(subjectType: SubjectType): Surface {
  switch (subjectType) {
    case 'issue_comment':
      return 'issue_comment'
    case 'issue':
      return 'issue_body'
    case 'pull_request':
      return 'pull_request'
    case 'commit':
      return 'commit'
  }
}

// url format is
// https://github.com/:owner:/:repo:/slash_apps?subject_gid=:global_relay_id:&surface=issue_comment
const buildSlashCommandsUrl = (subject: RepoSubject) => {
  let url = `/${subject.repository.nwo}/slash_apps?surface=${getSurface(subject.type)}`
  if (subject.id) {
    url += `&subject_gid=${subject.id.id}`
  }
  return url
}

export const SlashCommandsProvider = ({children, subject, onInsertText, onSave}: SlashCommandProviderProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const expanderRef = useRef<SlashCommandExpanderElement>(null)

  // The slash-command-expander positions its menu using the offsets of the slash character
  // that triggered it, relative to the textarea containing that character. But because the menu
  // is position:absolute and the nearest offset parent is the Box container, it does not take
  // into account any margin around the textarea, the toolbar, etc. So we need to adjust for the
  // offset of the textarea relative to the container Box.
  const [{top, left}, setPosition] = useState({top: 0, left: 0})
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Go up through the parent tree adding offsets until we get to the container Box
    let target = container?.querySelector<HTMLElement>('textarea')
    const position = {top: 0, left: 0}
    while (target !== container) {
      if (target === null) return
      position.top += target.offsetTop
      position.left += target.offsetLeft
      target = target.offsetParent instanceof HTMLElement ? target.offsetParent : null
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPosition(position)
  }, []) // this won't recalculate if things change (ie, the window is resized so toolbar buttons wrap) but that should be a rare scenario

  // Ensure the expander reconnects to the input element when React updates the component
  useEffect(() => {
    const expander = expanderRef.current
    if (!expander) return

    // Initially connect to the textarea
    const initialTextarea = containerRef.current?.querySelector('textarea')
    if (initialTextarea) {
      expander.reconnect?.()
    }

    // When the editor focuses/activates, ensure the connection is established
    const handleFocus = () => {
      // Only reconnect if there's no active menu
      if (!expander.querySelector('.js-slash-command-menu:not(.d-none)')) {
        expander.reconnect?.()
      }
    }

    // Add focus event listener to textarea
    const textarea = containerRef.current?.querySelector('textarea')
    textarea?.addEventListener('focus', handleFocus)

    return () => {
      textarea?.removeEventListener('focus', handleFocus)
    }
  }, [])

  // Monitor for textarea changes due to React re-rendering
  useEffect(() => {
    const expander = expanderRef.current
    if (!expander) return

    let lastTextarea = containerRef.current?.querySelector('textarea') || null

    // Set up a MutationObserver that's very selective about reconnecting
    const observer = new MutationObserver(() => {
      const currentTextarea = containerRef.current?.querySelector('textarea')

      // Only reconnect if the textarea has actually changed and there's no active menu
      if (currentTextarea && currentTextarea !== lastTextarea) {
        lastTextarea = currentTextarea

        // Don't interrupt an active menu
        if (!expander.querySelector('.js-slash-command-menu:not(.d-none)')) {
          expander.reconnect?.()
        }
      }
    })

    if (containerRef.current) {
      observer.observe(containerRef.current, {childList: true, subtree: true})
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!onSave) return

    const expander = expanderRef.current
    if (!expander) return

    expander.addEventListener('request-submit', onSave)

    return () => {
      expander.removeEventListener('request-submit', onSave)
    }
  }, [onSave])

  const contextValue = useMemo(() => ({onInsertText}), [onInsertText])

  return (
    <Box
      ref={containerRef}
      sx={{
        '& .js-slash-command-menu': {mt: `${top}px`, ml: `${left}px`},
      }}
      className={clsx('js-slash-command-surface', styles.slashCommandContainer)}
    >
      <slash-command-expander ref={expanderRef} keys="/" data-slash-command-url={buildSlashCommandsUrl(subject)}>
        <SlashCommandButtonContext value={contextValue}>{children}</SlashCommandButtonContext>
      </slash-command-expander>
    </Box>
  )
}

export const SlashCommandsButton = () => {
  const context = useContext(SlashCommandButtonContext)
  return context ? (
    <MarkdownEditor.ToolbarButton
      icon={DiffIgnoredIcon}
      aria-label="Slash commands"
      onClick={() => context.onInsertText('/')}
    />
  ) : null
}

try{ SlashCommandButtonContext.displayName ||= 'SlashCommandButtonContext' } catch {}
try{ SlashCommandsProvider.displayName ||= 'SlashCommandsProvider' } catch {}
try{ SlashCommandsButton.displayName ||= 'SlashCommandsButton' } catch {}